import visionCallService from "../services/visionCallService";
import JsonApiService from "../services/jsonapiService";
import { visionURL } from "../config/environment";
import { useEffect, useState } from "react";
import Jsona from "jsona";

const dataFormatter = new Jsona();

const groupsApiService = async () =>
  new JsonApiService("groups", {
    baseUrl: `${visionURL}/admin_console/api/administration/groups`,
    credentials: "include"
  });

const groupsPositionsApiService = async () =>
  new JsonApiService("groups_positions", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

const groupsLocationsApiService = async () =>
  new JsonApiService("groups_locations", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

export const useGroup = groupId => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [group, setGroup] = useState(null);

  const fetchGroup = async () => {
    if (groupId) {
      setIsLoading(true);
      const service = await groupsApiService();
      const response = await service
        .include([
          "locations",
          "positions",
          "leaders",
          "leaders.position",
          "leaders.location",
          "leaders.customer",
          "groups_positions",
          "groups_locations",
          "positions.customer",
          "customer",
          "locations.customer"
        ])
        .find(groupId)
        .then(response => response.json());
      setIsLoading(false);
      setGroup(dataFormatter.deserialize(response));
    }
  };

  useEffect(() => {
    fetchGroup();
  }, [groupId]);

  const deleteGroup = async groupId => {
    setIsDeleting(true);
    const service = await groupsApiService();
    const response = await service.delete(groupId);
    setIsDeleting(false);
    return response;
  };

  const batchUpdateGroupPositions = async (
    groupPositionsToCreate,
    groupPositionsToDelete
  ) => {
    const service = await groupsPositionsApiService();

    const getData = (groupPositions, operation) => {
      return groupPositions
        ? groupPositions.map(data => {
            return visionCallService.serializeAtomicOperation(
              data,
              "groupsPosition",
              operation,
              ["group", "position"]
            );
          })
        : [];
    };
    const operations = [
      ...getData(groupPositionsToCreate, "add"),
      ...getData(groupPositionsToDelete, "remove")
    ];
    const response = await service.operations(operations);
    const operationData = await response.json();
    return operationData;
  };
  const batchUpdateGroupLocations = async (
    groupLocationsToCreate,
    groupLocationsToDelete
  ) => {
    const service = await groupsLocationsApiService();

    const getData = (groupLocations, operation) => {
      return groupLocations
        ? groupLocations.map(data => {
            return visionCallService.serializeAtomicOperation(
              data,
              "groupsLocation",
              operation,
              ["group", "location"]
            );
          })
        : [];
    };
    const operations = [
      ...getData(groupLocationsToCreate, "add"),
      ...getData(groupLocationsToDelete, "remove")
    ];
    const response = await service.operations(operations);
    const operationData = await response.json();
    return operationData;
  };

  return {
    group,
    fetchGroup,
    deleteGroup,
    batchUpdateGroupPositions,
    batchUpdateGroupLocations,
    isLoading,
    isDeleting
  };
};

export const useGroupAverages = (groupId, trainingObjectType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [groupAverages, setGroupAverages] = useState(null);

  useEffect(() => {
    const fetchGroupAverages = async () => {
      if (groupId) {
        setIsLoading(true);
        const service = await groupsApiService();
        const response = await service
          .customParams({ training_object_type: trainingObjectType })
          .related(groupId, "group_averages")
          .then(response => response.json());
        setIsLoading(false);
        setGroupAverages(response);
      }
    };
    fetchGroupAverages();
  }, [groupId, trainingObjectType]);

  return {
    groupAverages,
    isLoading
  };
};

export const fetchGroups = () => {
  const [isLoading, setIsLoading] = useState(false);
  return {
    getGroups: async ({ page, sort, filters }) => {
      setIsLoading(true);
      const service = await groupsApiService();
      const response = await service
        .page(page)
        .sort(sort)
        .where(filters)
        .all()
        .then(response => response.json());
      setIsLoading(false);
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta
      };
    },
    getGroup: async groupId => {
      const service = await groupsApiService();
      const response = await service
        .include([
          "locations",
          "positions",
          "leaders",
          "leaders.position",
          "leaders.location"
        ])
        .find(groupId)
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    deleteGroup: async groupId => {
      const service = await groupsApiService();
      const response = await service
        .delete(groupId)
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    isLoading
  };
};

export const createGroup = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  return {
    createNewGroup: async data => {
      setIsSubmitting(true);
      const service = await groupsApiService();
      const response = await service
        .create(JSON.stringify(data))
        .then(response => response.json());
      const submissionData = dataFormatter.deserialize(response);
      setIsSubmitting(false);
      return submissionData;
    },
    isSubmitting
  };
};
